//Controllers App
import api from "../api/endpoints";
import logger from "../utils/logger";

/**
 * Actualizar estado de la app validando token y recargando usuario desde API.
 * Esto asegura que siempre tenemos datos frescos del backend y no datos obsoletos de localStorage.
 */
const UpdateApp = async (dispatch) => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");
  const algorithm = "dark";

  console.log("[UpdateApp] Iniciando carga de app, token existe:", !!token);

  // Fetch global config regardless of auth status (for login screen logo/theme)
  // Esto asegura que el login siempre muestre los colores y logo correctos
  try {
    const configData = await api.config.get();
    if (configData) {
      // Cargar configuraciones guardadas localmente
      const savedAppTitle = localStorage.getItem("appTitle");
      const savedAlgorithm = localStorage.getItem("algorithm") || "light";
      const savedFontSize = localStorage.getItem("fontSize");
      const savedFontFamily = localStorage.getItem("fontFamily");
      const savedComponentSize = localStorage.getItem("componentSize");
      const savedBorderRadius = localStorage.getItem("borderRadius");
      const savedMotion = localStorage.getItem("motion");
      const savedCompact = localStorage.getItem("compact");

      // Usar app_name del backend si no hay uno guardado localmente
      const finalAppTitle =
        savedAppTitle || configData.app_name || "ERP - Smart APR";

      // Obtener logo - priorizar logo_url (URL completa) sobre global_logo
      const logoUrl = configData.logo_url || configData.global_logo || null;

      const themeConfig = configData.theme_config || {};

      const resolvedFontSize =
        themeConfig.font_size !== undefined && themeConfig.font_size !== null
          ? themeConfig.font_size
          : savedFontSize
            ? parseInt(savedFontSize)
            : 14;
      const resolvedComponentSize =
        themeConfig.density || savedComponentSize || "middle";
      const resolvedBorderRadius =
        themeConfig.borderRadius !== undefined &&
        themeConfig.borderRadius !== null
          ? themeConfig.borderRadius
          : savedBorderRadius
            ? parseInt(savedBorderRadius)
            : 6;
      const resolvedMotion =
        themeConfig.animations !== undefined && themeConfig.animations !== null
          ? themeConfig.animations !== false
          : savedMotion !== null
            ? savedMotion !== "false"
            : true;
      const resolvedCompact =
        themeConfig.compactMode !== undefined && themeConfig.compactMode !== null
          ? themeConfig.compactMode === true
          : savedCompact !== null
            ? savedCompact === "true"
            : false;
      const resolvedFontFamily = themeConfig.fontFamily || savedFontFamily;
      const globalLogoType = themeConfig.logoType || "nucleus";

      // Cargar colores configurables desde backend o localStorage
      const savedRoleColors = localStorage.getItem("roleColors");
      const savedSemanticColors = localStorage.getItem("semanticColors");
      const savedGradientColors = localStorage.getItem("gradientColors");
      const savedStatusColors = localStorage.getItem("statusColors");

      // Helper para parsear JSON de forma segura
      const safeParse = (str, fallback = undefined) => {
        if (!str) return fallback;
        try {
          return typeof str === "string" ? JSON.parse(str) : str;
        } catch (e) {
          logger.warn("Error parsing JSON:", e);
          return fallback;
        }
      };

      dispatch({
        type: "SET_THEME",
        payload: {
          primaryColor: configData.primary_color || "#1890ff", // Azul por defecto, no negro
          secondaryColor: configData.secondary_color || "#ffffff",
          logo: logoUrl,
          globalLogoType,
          appTitle: finalAppTitle,
          algorithm: savedAlgorithm,
          fontSize: resolvedFontSize,
          fontFamily: resolvedFontFamily,
          componentSize: resolvedComponentSize,
          borderRadius: resolvedBorderRadius,
          motion: resolvedMotion,
          compact: resolvedCompact,
          // Cargar colores configurables desde backend o localStorage
          roleColors: configData.role_colors
            ? safeParse(configData.role_colors)
            : safeParse(savedRoleColors),
          semanticColors: configData.semantic_colors
            ? safeParse(configData.semantic_colors)
            : safeParse(savedSemanticColors),
          gradientColors: configData.gradient_colors
            ? safeParse(configData.gradient_colors)
            : safeParse(savedGradientColors),
          statusColors: configData.status_colors
            ? safeParse(configData.status_colors)
            : safeParse(savedStatusColors),
        },
      });

      // Actualizar título del documento
      if (typeof document !== "undefined") {
        document.title = finalAppTitle;
      }
    } else {
      // Si no hay configuración en el backend, mantener valores por defecto
      // pero asegurar que los colores estén en el estado
      const savedPrimaryColor = localStorage.getItem("primaryColor");
      const savedSecondaryColor = localStorage.getItem("secondaryColor");
      const savedLogo = localStorage.getItem("logo");

      if (savedPrimaryColor || savedSecondaryColor || savedLogo) {
        dispatch({
          type: "SET_THEME",
          payload: {
            primaryColor: savedPrimaryColor || "#1890ff",
            secondaryColor: savedSecondaryColor || "#ffffff",
            logo: savedLogo || null,
          },
        });
      }
    }
  } catch (error) {
    // Si hay error, usar valores guardados localmente como fallback
    logger.warn(
      "Error loading global config, using local storage values:",
      error,
    );
    const savedPrimaryColor = localStorage.getItem("primaryColor");
    const savedSecondaryColor = localStorage.getItem("secondaryColor");
    const savedLogo = localStorage.getItem("logo");

    if (savedPrimaryColor || savedSecondaryColor || savedLogo) {
      dispatch({
        type: "SET_THEME",
        payload: {
          primaryColor: savedPrimaryColor || "#1890ff",
          secondaryColor: savedSecondaryColor || "#ffffff",
          logo: savedLogo || null,
        },
      });
    }
  }

  // Si no hay token, no hacer nada más
  if (!token) {
    console.log("[UpdateApp] No hay token, terminando");
    return;
  }

  try {
    // ⚡ Validar token y recargar usuario desde la API (no usar localStorage obsoleto)
    // Esto asegura que si el token expiró, haremos logout automático
    // Y si es válido, tendremos datos actualizados del usuario (incluyendo is_superuser)
    console.log("[UpdateApp] Cargando perfil de usuario...");
    const userData = await api.users.get_my_profile();
    console.log("[UpdateApp] Perfil cargado:", {
      userId: userData?.id,
      isSuperuser: userData?.is_superuser,
      typeUser: userData?.type_user,
      hasBranchAssignments: !!userData?.branch_assignments?.length,
    });

    // Verificar que obtuvimos datos válidos
    if (userData && userData.id) {
      // Actualizar estado con datos frescos del backend
      dispatch({
        type: "LOGIN",
        payload: {
          token,
          user: userData, // Datos actualizados desde la API
          algorithm:
            userData.is_superuser || userData.type_user === "ADM"
              ? "dark"
              : algorithm,
        },
      });

      // Cargar configuración de sucursal predeterminada
      // ANTES: Solo para no-superadmins. AHORA: Para todos los usuarios autenticados
      // que tengan sucursales asignadas, independientemente de si son superuser
      console.log("[UpdateApp] Iniciando carga de sucursal...");
      try {
        console.log("[UpdateApp] Llamando a getMyDefaultBranchTheme...");
        const branchTheme = await api.config.getMyDefaultBranchTheme();
        console.log("[UpdateApp] Branch theme recibido:", {
          defaultBranchId: branchTheme?.default_branch_id,
          appName: branchTheme?.app_name,
        });

        // ⚡ LÓGICA ROBUSTA PARA DETERMINAR SUCURSAL ACTIVA
        // 1. Obtener lista de sucursales permitidas
        let branchesList = [];
        try {
          console.log("[UpdateApp] Llamando a my_branches...");
          const branchesResponse = await api.branchs.my_branches();
          console.log("[UpdateApp] Respuesta my_branches:", branchesResponse);
          
          const rawBranches = branchesResponse?.data ?? branchesResponse ?? [];

          if (Array.isArray(rawBranches)) {
            branchesList = rawBranches;
          } else if (Array.isArray(rawBranches.results)) {
            branchesList = rawBranches.results;
          } else if (Array.isArray(rawBranches.data?.results)) {
            branchesList = rawBranches.data.results;
          } else {
            branchesList = [];
          }

          console.log("[UpdateApp] Sucursales parseadas:", branchesList.length);
          
          dispatch({
            type: "SET_BRANCHES",
            payload: branchesList,
          });
        } catch (branchError) {
          console.error("[UpdateApp] Error cargando sucursales:", branchError);
          logger.warn("Error loading global branches list:", branchError);
        }

        // Si el usuario no tiene sucursales, no continuar con la lógica de sucursal
        if (branchesList.length === 0) {
          console.log("[UpdateApp] Usuario sin sucursales asignadas");
        } else {
          console.log("[UpdateApp] Procesando sucursales...");
          
          // 2. Determinar el ID candidato de la sesión (no debe pisar la predeterminada al volver a entrar)
          const savedActiveBranchId = sessionStorage.getItem("activeBranchId")
            ? parseInt(sessionStorage.getItem("activeBranchId"))
            : null;

          console.log("[UpdateApp] Saved active branch ID:", savedActiveBranchId);

          // 3. Validar si el ID guardado sigue siendo válido (el usuario tiene acceso)
          // O si la sucursal está marcada como 'is_default' en la lista (prioridad backend)
          let activeBranchId = null;

          // Buscar si hay una sucursal marcada explícitamente como default en la lista
          const defaultBranchInList = branchesList.find((b) => b.is_default);
          console.log("[UpdateApp] Default branch en lista:", defaultBranchInList);

          if (savedActiveBranchId) {
            // Verificar si el usuario aún tiene acceso a la sucursal guardada
            const isValid = branchesList.some(
              (b) => (b.branch ? b.branch.id : b.id) === savedActiveBranchId,
            );

            console.log("[UpdateApp] Saved branch válido:", isValid);

            if (isValid) {
              // Si es válida, usarla (mantiene la persistencia local si es correcta)
              activeBranchId = savedActiveBranchId;
            } else {
              // Si no es válida (ej: le quitaron permisos), usar la default del backend
              logger.warn(
                `Sucursal guardada ${savedActiveBranchId} ya no es válida.`,
              );
              activeBranchId = defaultBranchInList
                ? defaultBranchInList.branch
                  ? defaultBranchInList.branch.id
                  : defaultBranchInList.id
                : branchTheme.default_branch_id;
            }
          } else {
            // Si no hay nada guardado, usar la default del backend
            activeBranchId = defaultBranchInList
              ? defaultBranchInList.branch
                ? defaultBranchInList.branch.id
                : defaultBranchInList.id
              : branchTheme.default_branch_id;
          }

          console.log("[UpdateApp] Active branch ID determinado:", activeBranchId);

          // 4. Fallback final: Si aún no hay ID (usuario nuevo o sin default), usar la primera disponible
          if (!activeBranchId && branchesList.length > 0) {
            const firstBranch = branchesList[0];
            activeBranchId = firstBranch.branch
              ? firstBranch.branch.id
              : firstBranch.id;
            console.log("[UpdateApp] Usando primera sucursal:", activeBranchId);
          }

          // Guardar la decisión final en sessionStorage para sincronizar
          if (activeBranchId) {
            console.log("[UpdateApp] Guardando activeBranchId:", activeBranchId);
            sessionStorage.setItem("activeBranchId", String(activeBranchId));
            console.log("[UpdateApp] Haciendo dispatch SET_CURRENT_BRANCH...");
            dispatch({
              type: "SET_CURRENT_BRANCH",
              payload: activeBranchId,
            });
            console.log("[UpdateApp] Dispatch completado");
          } else {
            console.warn("[UpdateApp] No se pudo determinar activeBranchId");
          }

          // Si la sucursal activa es diferente a la default, cargar su tema específico
          let themeToApply = branchTheme;

          if (
            activeBranchId &&
            activeBranchId !== branchTheme.default_branch_id
          ) {
            try {
              const activeTheme =
                await api.branchs.getBranchThemeConfig(activeBranchId);
              if (activeTheme && (activeTheme.data || activeTheme)) {
                themeToApply = activeTheme.data || activeTheme;
              }
            } catch (themeError) {
              logger.warn("Error loading active branch theme:", themeError);
              // Fallback to default theme if active theme fails
            }
          }

          // Aplicar configuración temática (incluye colores configurables si vienen del backend)
          // Solo aplicar tema de sucursal si NO estamos en la ruta de admin global
          const isGlobalAdminRoute =
            typeof window !== "undefined" &&
            window.location.pathname.startsWith("/app/global-admin");

          if (!isGlobalAdminRoute) {
            dispatch({
              type: "SET_BRANCH_THEME",
              payload: {
                ...themeToApply,
                role_colors: themeToApply.role_colors
                  ? typeof themeToApply.role_colors === "string"
                    ? JSON.parse(themeToApply.role_colors)
                    : themeToApply.role_colors
                  : undefined,
                semantic_colors: themeToApply.semantic_colors
                  ? typeof themeToApply.semantic_colors === "string"
                    ? JSON.parse(themeToApply.semantic_colors)
                    : themeToApply.semantic_colors
                  : undefined,
                gradient_colors: themeToApply.gradient_colors
                  ? typeof themeToApply.gradient_colors === "string"
                    ? JSON.parse(themeToApply.gradient_colors)
                    : themeToApply.gradient_colors
                  : undefined,
                status_colors: themeToApply.status_colors
                  ? typeof themeToApply.status_colors === "string"
                    ? JSON.parse(themeToApply.status_colors)
                    : themeToApply.status_colors
                  : undefined,
              },
            });
          }

          // Actualizar título del documento con nombre de sucursal si está disponible
          if (typeof document !== "undefined") {
            const title =
              themeToApply.app_name ||
              themeToApply.branch_name ||
              document.title;
            document.title = title;
          }
        }
      } catch (error) {
        console.error("[UpdateApp] Error cargando branch theme:", error);
        logger.warn("Error loading default branch theme:", error);
        // Continuar sin configuración de sucursal si hay error
      }
      
      console.log("[UpdateApp] Carga de app completada");
    } else {
      // Si no hay datos válidos, limpiar y hacer logout
      logger.warn("No se pudo cargar perfil del usuario");
      dispatch({ type: "LOGOUT" });
    }
  } catch (error) {
    // Si hay error (token inválido, expirado, etc.), hacer logout automático
    logger.error("Error validando sesión:", error);

    // Limpiar localStorage y estado
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });

    // Solo redirigir a login si no estamos ya ahí
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  // Si llegamos aquí, devolver los datos del usuario para App.js
  const userData = await api.users.get_my_profile().catch(() => null);
  return { data: { user: userData } };
};

export const controller = {
  update_app: UpdateApp,
};
